import {
  BlockType,
  ColorSchemeSchema,
  FieldGroupSchema,
  FieldRole,
  ImageFieldSchema,
  RichTextFieldSchema,
  UrlFieldSchema,
} from './Types';
import { z } from 'zod';

export const ResourceLibraryStyleOptionsSchema = z.object({
  backgroundColor: z.string().optional(),
});

export type ResourceLibraryStyleOptions = z.infer<typeof ResourceLibraryStyleOptionsSchema>;

export const ResourceLibraryOptionsSchema = z.object({
  colorScheme: ColorSchemeSchema.optional(),
  preheaderEnabled: z.boolean().optional(),
  subheaderEnabled: z.boolean().optional(),
  entryCount: z.number().optional(),
  style: ResourceLibraryStyleOptionsSchema.optional(),
  layout: z.string().optional(),
});

export type ResourceLibraryOptions = z.infer<typeof ResourceLibraryOptionsSchema>;

export const ResourceLibraryTopLevelPreheaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('preheader'),
  role: z.literal(FieldRole.Preheader),
});

export const ResourceLibraryTopLevelHeaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('header'),
  role: z.literal(FieldRole.Header),
});

export const ResourceLibraryTopLevelSubheaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('subheader'),
  role: z.literal(FieldRole.Subheader),
});

export const ResourceLibraryHeaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('header'),
  role: z.literal(FieldRole.Header),
});

export const ResourceLibraryDescriptionFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('description'),
  role: z.literal(FieldRole.Description),
});

export const ResourceLibraryImageFieldSchema = ImageFieldSchema.extend({
  name: z.literal('image'),
  role: z.literal(FieldRole.Image),
});

export const ResourceLibraryLinkFieldSchema = UrlFieldSchema.extend({
  name: z.literal('link'),
  role: z.literal(FieldRole.Url),
});

export const ResourceLibraryTopLevelFieldSchema = z.discriminatedUnion('name', [
  ResourceLibraryTopLevelPreheaderFieldSchema,
  ResourceLibraryTopLevelHeaderFieldSchema,
  ResourceLibraryTopLevelSubheaderFieldSchema,
]);

export type ResourceLibraryTopLevelField = z.infer<typeof ResourceLibraryTopLevelFieldSchema>;

export const ResourceLibraryFieldSchema = z.discriminatedUnion('name', [
  ResourceLibraryHeaderFieldSchema,
  ResourceLibraryDescriptionFieldSchema,
  ResourceLibraryImageFieldSchema,
  ResourceLibraryLinkFieldSchema,
]);
export type ResourceLibraryField = z.infer<typeof ResourceLibraryFieldSchema>;

export const ResourceFieldsSchema = z.discriminatedUnion('name', [
  ResourceLibraryDescriptionFieldSchema,
  ResourceLibraryHeaderFieldSchema,
  ResourceLibraryImageFieldSchema,
  ResourceLibraryLinkFieldSchema,
]);

export type ResourceFields = z.infer<typeof ResourceFieldsSchema>;

export const ResourceLibraryTopLevelFieldGroupSchema = FieldGroupSchema.extend({
  fields: z.array(ResourceLibraryTopLevelFieldSchema),
});
export type ResourceLibraryTopLevelFieldGroup = z.infer<typeof ResourceLibraryTopLevelFieldGroupSchema>;

export const ResourceLibraryResourceFieldsGroupSchema = FieldGroupSchema.extend({
  fields: z.array(ResourceLibraryFieldSchema),
});
export type ResourceLibraryResourceFieldsGroup = z.infer<typeof ResourceLibraryResourceFieldsGroupSchema>;

export const ResourceLibraryBlockConfigSchema = z.object({
  options: ResourceLibraryOptionsSchema,
  type: z.literal(BlockType.ResourceLibrary),
  fieldGroups: z.tuple([ResourceLibraryTopLevelFieldGroupSchema]).rest(ResourceLibraryResourceFieldsGroupSchema),
  version: z.literal(1),
});
